import React from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { apiUtils } from '../services/api';
import { useData } from '../contexts/DataContext';

/**
 * Envuelve una ruta que requiere un permiso de negocio específico.
 * - Dueños de empresa (myBusinesses.length > 0) → acceso siempre
 * - Asociados → solo si tienen el permiso en alguna asociación activa
 * - Sin autenticar → redirige a login
 * - Sin permiso → redirige a /dashboard
 *
 * @param {string} permission - 'can_edit_products' | 'can_edit_services' | 'can_manage_reviews' | 'can_view_analytics' | 'owner_only'
 */
const PermissionRoute = ({ permission, children }) => {
    const { myBusinesses, activeAssociations, businessesLoading } = useData();
    const location = useLocation();

    if (!apiUtils.isAuthenticated()) {
        // Preservar la ruta intentada como ?next= para volver ahí tras el
        // re-login. El banner "Tu sesión expiró" lo pinta la página de login
        // leyendo ?reason=expired (ver pages/user-authentication/index.jsx).
        const next = encodeURIComponent(location.pathname + location.search);
        return <Navigate to={`/user-authentication?reason=expired&next=${next}`} replace />;
    }

    // Esperar a que carguen los businesses antes de decidir — evita redirect falso al recargar
    if (businessesLoading) {
        return null;
    }

    // Dueños de empresa → acceso completo
    if (myBusinesses && myBusinesses.length > 0) {
        return children;
    }

    // owner_only → owner real o asociado activo. La ruta sigue siendo el editor
    // "completo" (basic + location + gallery + verification), pero la decisión
    // final de qué se muestra vive en BusinessProfilePage:
    //   - Si la empresa tiene owner real (no preclaimed) y el user no es owner
    //     → redirige a /quick-edit (evita filtrar docs legales del dueño).
    //   - Si la empresa es preclaimed (owner = admin de plataforma, sin dueño
    //     real todavía) → el asociado activo ve el editor completo sin la
    //     sección Verificación. Misma UX que owner, menos lo legal.
    if (permission === 'owner_only') {
        if ((activeAssociations?.length || 0) > 0) {
            return children;
        }
        return <Navigate to="/dashboard" replace />;
    }

    // any_business_relation → ver el listado/agrupado de empresas. Cualquier
    // user con asociación activa puede llegar (no necesita los 4 perms base);
    // owners ya pasaron por el shortcut de arriba. Ej.: /dashboard/business
    // que muestra owned + asociadas y deja a cada card gatear sus acciones.
    if (permission === 'any_business_relation') {
        if ((activeAssociations?.length || 0) > 0) {
            return children;
        }
        return <Navigate to="/dashboard" replace />;
    }

    // Asociados → verificar permiso específico
    const hasPermission = activeAssociations?.some(a => a[permission]);
    if (hasPermission) {
        return children;
    }

    return <Navigate to="/dashboard" replace />;
};

export default PermissionRoute;
